import { Rocket, Crown, Megaphone, Check } from "lucide-react";
import { SiteCTA } from "./shared";
import { NfcCard3D } from "./NfcCard3D";

// Shared plan data + card grid — used by the standalone /pricing page and the
// "Pricing" section embedded on the homepage, so the two never drift apart.
export const PLANS = [
  {
    key: "core",
    icon: Rocket,
    name: "Google Boost",
    phase: "Phase 1 · Where everyone starts",
    price: "$79",
    priceSuffix: "one-time",
    priceNote: "No subscription · rank higher in 7 days or your money back",
    cta: "Start Google Boost — $79",
    ctaHref: "/start?plan=core",
    items: [
      "Day 1 — your before heatmap: see exactly where you rank across your city today",
      "Day 2 — full Google profile optimization (SEO, AEO & GMB tune-up)",
      "Days 2–7 — geotagged Google posts + we reply to every review and text past customers for more",
      "Day 7 — results call: your before & after heatmap, side by side",
    ],
    note: "A one-time 7-day sprint — no commitment, no subscription.",
  },
  {
    key: "pro",
    icon: Crown,
    name: "Slammed Pro",
    phase: "Phase 2 · Grow faster",
    price: "$450",
    priceSuffix: "/mo",
    priceNote: "Month to month · cancel anytime · rank #1 in 90 days or your money back",
    featured: true,
    cta: "Start Pro — $450/mo",
    ctaHref: "/start?plan=pro_full",
    items: [
      "Daily Google optimization — SEO, AEO & GMB work every single day, the fastest path to #1",
      "A new optimized website that books for you — live in about 24 hours, with an AI chat widget",
      "Reviews done for you — we reply to every review and text your past customers for more",
      "Recommended on AI search — show up when locals ask ChatGPT, Gemini & Google AI for a local pro",
      "Social media management — we post daily with your geotagged business photos to boost local SEO",
      "Monthly ranking heatmaps — watch your pins turn green across your city",
      "Listed on 62 directories — Google, Apple Maps, Bing, Yelp, Waze, Nextdoor & more",
      "Keyword + AI rank reports (ChatGPT & Gemini) and weekly SEO reports in your inbox",
      "Private group chat with your dedicated Slammed team — text us anytime",
    ],
    note: "For businesses ready to grow faster.",
    gift: true,
  },
  {
    key: "elite",
    icon: Megaphone,
    name: "Slammed Elite",
    phase: "Phase 3 · By qualification",
    price: "$2,500",
    priceSuffix: "/mo",
    priceNote: "By qualification only",
    cta: "Book an onboarding call",
    ctaHref: "/contact",
    items: [
      "Everything in Pro, plus:",
      "Your own custom-built CRM + branded app",
      "Missed-call text-back & 24/7 AI lead response — every lead answered day and night",
      "Your own appointment setter — hired and trained on YOUR offer by us",
      "Done-for-you Meta & Google ad campaigns, managed end to end",
      "20+ fresh ad images and copy every month, retargeting, strategy calls + full reporting",
    ],
    note: "We only take Elite clients whose foundation already converts — so ad dollars become booked jobs, not burned money.",
  },
];

function PlanCard({ plan }) {
  const { key, icon: Icon, name, phase, price, priceSuffix, priceNote, items, note, featured, cta, ctaHref } = plan;
  return (
    <div
      data-testid={`pricing-plan-${key}`}
      className={`relative flex w-full flex-col rounded-3xl p-7 sm:p-8 ${
        featured
          ? "bg-navy text-white shadow-[0_24px_70px_-20px_rgba(14,18,48,0.4)] lg:-my-3 lg:py-11"
          : "border border-navy/[0.06] bg-white shadow-[0_8px_40px_rgba(14,18,48,0.05)]"
      }`}
    >
      {featured && (
        <span className="absolute right-5 top-5 rounded-full bg-brand-gradient px-2.5 py-1 font-mono text-[8.5px] font-bold uppercase tracking-[0.14em] text-white">Most popular</span>
      )}
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${featured ? "bg-brand-gradient" : "bg-brand-indigo/10"}`}>
          <Icon className={`h-5 w-5 ${featured ? "text-white" : "text-brand-indigo"}`} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className={`font-display text-xl font-extrabold tracking-tight ${featured ? "text-white" : "text-navy"}`}>{name}</h2>
          <p className={`font-mono text-[10px] font-bold uppercase tracking-[0.16em] ${featured ? "text-brand-sky/80" : "text-brand-indigo"}`}>{phase}</p>
        </div>
      </div>

      <div className={`mt-5 flex flex-wrap items-baseline gap-x-2 border-b pb-5 ${featured ? "border-white/10" : "border-navy/[0.07]"}`}>
        <p data-testid={`pricing-price-${key}`} className={`font-display text-4xl font-extrabold tracking-tight sm:text-5xl ${featured ? "text-white" : "text-navy"}`}>
          {price} <span className={`text-xl font-bold ${featured ? "text-white/60" : "text-navy/45"}`}>{priceSuffix}</span>
        </p>
        <p className={`w-full pt-1 text-[12.5px] font-bold ${featured ? "text-white/55" : "text-navy/50"}`}>{priceNote}</p>
      </div>

      <ul className="mt-5 flex-1 space-y-2.5">
        {items.map((t) => (
          <li key={t} className={`flex items-start gap-2.5 text-[13.5px] font-semibold leading-snug ${featured ? "text-white/80" : "text-navy/70"}`}>
            <Check className={`mt-0.5 h-4 w-4 shrink-0 ${featured ? "text-emerald-400" : "text-emerald-500"}`} />{t}
          </li>
        ))}
      </ul>

      {plan.gift && (
        <div className="mt-5 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-left" data-testid={`pricing-gift-${key}`}>
          <NfcCard3D imgClassName="h-12 w-12" testId="pricing-nfc" />
          <div className="min-w-0">
            <p className="text-[12.5px] font-extrabold text-navy">Free when you start: NFC review card ($30 value)</p>
            <p className="mt-0.5 text-[11.5px] leading-snug text-navy/60">Customers tap it with their phone — your Google review page opens. Nothing to set up; we mail it to you.</p>
          </div>
        </div>
      )}

      {note && <p className={`mt-5 border-t pt-4 text-[12px] leading-snug ${featured ? "border-white/10 text-white/50" : "border-navy/[0.07] text-navy/45"}`}>{note}</p>}

      <div className="mt-6">
        <SiteCTA href={ctaHref} testId={`pricing-cta-${key}`} className="w-full" dark={featured}>
          {cta}
        </SiteCTA>
      </div>
    </div>
  );
}

export function PricingCards() {
  return (
    <div className="grid items-start gap-6 lg:grid-cols-3 lg:gap-5">
      {PLANS.map((p) => <PlanCard key={p.key} plan={p} />)}
    </div>
  );
}
